import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api, { meScope } from '../lib/api';
import { getThemeColors, BRAND } from '../lib/theme';

// ─── Types ────────────────────────────────────────────────────────────────────

type Locale = 'de' | 'en' | 'pl';

interface Language {
  locale: Locale;
  /** Native name shown in the dropdown (not translated — intentional per spec) */
  nativeName: string;
}

const LANGUAGES: Language[] = [
  { locale: 'de', nativeName: 'Deutsch' },
  { locale: 'en', nativeName: 'English' },
  { locale: 'pl', nativeName: 'Polski' },
];

// ─── Props ────────────────────────────────────────────────────────────────────

interface LanguageSwitcherProps {
  dark?: boolean;
  /** 'header' = icon-style button; 'footer' = text link style (for login page) */
  variant?: 'header' | 'footer';
  /**
   * Nur header-Variante: schmale Darstellung (Globus + Sprachkürzel, kein
   * Chevron) für die Handy-Navbar, damit links Platz für Zurück-Button bleibt.
   */
  compact?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function LanguageSwitcher({ dark = false, variant = 'header', compact = false }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const tc = getThemeColors(dark);

  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentLocale = (i18n.resolvedLanguage ?? i18n.language ?? 'de').slice(0, 2) as Locale;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const changeLanguage = async (locale: Locale) => {
    setOpen(false);

    // (a) Switch i18next — no reload, instant re-render
    await i18n.changeLanguage(locale);

    // (b) Update html[lang] attribute for a11y / SEO
    document.documentElement.lang = locale;

    // (c) Persist in localStorage with ADR-0001 key
    localStorage.setItem('sh24.locale', locale);

    // (d) Persist to backend only when user is authenticated
    //     Backend endpoint: PATCH /v2/me/locale  { locale }  (US-I18N-02)
    if (isAuthenticated) {
      api
        .patch(`${meScope()}/me/locale`, { locale })
        .catch(() => {
          if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
            console.warn('[LanguageSwitcher] PATCH /me/locale failed — backend US-I18N-02 may not be deployed yet.');
          }
        });
    }
  };

  const currentNativeName =
    LANGUAGES.find(l => l.locale === currentLocale)?.nativeName ?? currentLocale.toUpperCase();

  if (variant === 'footer') {
    return (
      <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
        <button
          aria-label={t('language_switcher.label')}
          aria-expanded={open}
          aria-haspopup="listbox"
          onClick={() => setOpen(v => !v)}
          style={{
            background: 'none',
            border: 'none',
            color: tc.muted,
            fontSize: 11,
            cursor: 'pointer',
            padding: '4px 8px',
            borderRadius: 6,
            fontFamily: 'Outfit, sans-serif',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <GlobeIcon size={13} color={tc.muted} />
          {currentNativeName}
          <ChevronDown size={11} color={tc.muted} rotated={open} />
        </button>
        {open && (
          <DropdownList
            languages={LANGUAGES}
            currentLocale={currentLocale}
            dark={dark}
            tc={tc}
            onSelect={changeLanguage}
            position="above"
            t={t}
          />
        )}
      </div>
    );
  }

  // header variant
  return (
    <div ref={containerRef} style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
      <button
        aria-label={t('language_switcher.label')}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          border: `1px solid ${tc.border}`,
          background: tc.card,
          borderRadius: 8,
          padding: compact ? '7px 8px' : '7px 11px',
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: 500,
          color: tc.text,
          fontFamily: 'Outfit, sans-serif',
          height: 40,
        }}
      >
        <GlobeIcon size={14} color={tc.muted} />
        {compact
          ? <span>{currentLocale.toUpperCase()}</span>
          : <span>{currentNativeName}</span>}
        {!compact && <ChevronDown size={11} color={tc.muted} rotated={open} />}
      </button>
      {open && (
        <DropdownList
          languages={LANGUAGES}
          currentLocale={currentLocale}
          dark={dark}
          tc={tc}
          onSelect={changeLanguage}
          position="below"
          t={t}
        />
      )}
    </div>
  );
}

// ─── Shared dropdown list ─────────────────────────────────────────────────────

interface DropdownListProps {
  languages: Language[];
  currentLocale: Locale;
  dark: boolean;
  tc: ReturnType<typeof getThemeColors>;
  onSelect: (locale: Locale) => void;
  position: 'above' | 'below';
  t: (key: string) => string;
}

function DropdownList({ languages, currentLocale, tc, onSelect, position, t }: DropdownListProps) {
  return (
    <ul
      role="listbox"
      aria-label={t('language_switcher.label')}
      style={{
        position: 'absolute',
        [position === 'below' ? 'top' : 'bottom']: position === 'below' ? 44 : 36,
        right: 0,
        background: tc.card,
        border: `1px solid ${tc.border}`,
        borderRadius: 10,
        boxShadow: '0 8px 20px -6px rgba(16,24,40,0.15)',
        padding: '6px 0',
        minWidth: 150,
        zIndex: 1000,
        listStyle: 'none',
        margin: 0,
      }}
    >
      {languages.map(lang => {
        const isActive = lang.locale === currentLocale;
        return (
          <li key={lang.locale} role="option" aria-selected={isActive}>
            <button
              onClick={() => onSelect(lang.locale)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                padding: '8px 14px',
                background: 'none',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: isActive ? 600 : 400,
                color: isActive ? BRAND : tc.text,
                fontFamily: 'Outfit, sans-serif',
              }}
              onMouseEnter={e => {
                if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = tc.hover;
              }}
              onMouseLeave={e => {
                if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = 'none';
              }}
            >
              <span style={{ flex: 1 }}>{lang.nativeName}</span>
              {isActive && <CheckIcon color={BRAND} />}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// ─── Inline SVG helpers ───────────────────────────────────────────────────────

function GlobeIcon({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

function ChevronDown({ size = 12, color = 'currentColor', rotated = false }: { size?: number; color?: string; rotated?: boolean }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ transform: rotated ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function CheckIcon({ color = 'currentColor' }: { color?: string }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
